import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatSnackBar } from '@angular/material/snack-bar';
import pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { ToWords } from 'to-words';
import { ProcurmentService } from 'src/app/services/procurment.service';
import { MaterialModule } from 'src/app/shared/material.module';

pdfMake.vfs = pdfFonts.vfs;
const toWords = new ToWords();

@Component({
  selector: 'app-purchase-bill-print',
  standalone: true,
  imports: [CommonModule, MaterialModule, MatProgressBar],
  templateUrl: './purchase-bill-print.component.html',
  styleUrl: './purchase-bill-print.component.scss'
})
export class PurchaseBillPrintComponent implements OnInit, OnDestroy {
  billId!: number;
  billData: any;
  previewData: any = null;
  pdfDoc: any;
  pdfSrc: string | null = null;
  isDialogMode = false;
  includeSignature = false;
  isGenerating = false;
  private generatedBlobUrl: string | null = null;
  private originalHtmlOverflow = '';
  private originalBodyOverflow = '';

  constructor(
    private route: ActivatedRoute,
    private procurementService: ProcurmentService,
    private snackBar: MatSnackBar,
    @Optional() @Inject(MAT_DIALOG_DATA) private dialogData: { id?: number } | null,
    @Optional() private dialogRef: MatDialogRef<PurchaseBillPrintComponent> | null
  ) {}

  ngOnInit(): void {
    document.body.classList.add('invoice-preview-mode');
    this.originalHtmlOverflow = document.documentElement.style.overflow;
    this.originalBodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = 'auto';
    document.body.style.overflow = 'auto';

    this.isDialogMode = !!this.dialogData?.id;
    this.billId = Number(this.dialogData?.id ?? this.route.snapshot.paramMap.get('id'));
    if (this.billId) {
      this.loadPurchaseBillData();
    }
  }

  ngOnDestroy(): void {
    document.body.classList.remove('invoice-preview-mode');
    document.documentElement.style.overflow = this.originalHtmlOverflow;
    document.body.style.overflow = this.originalBodyOverflow;
    if (this.generatedBlobUrl) {
      URL.revokeObjectURL(this.generatedBlobUrl);
    }
  }

  loadPurchaseBillData(): void {
    this.procurementService.getPurchaseBillPrintById(this.billId).subscribe({
      next: (data: any) => {
        this.billData = Array.isArray(data) ? data[0] : data;
        this.previewData = this.buildPreviewData(this.billData || {});
        this.generatePdf();
      },
      error: (err) => console.error('Error loading purchase bill print', err)
    });
  }

  printPdf() {
    this.pdfDoc?.print();
  }

  downloadPdf() {
    this.pdfDoc?.download(`Purchase-Bill-${this.previewData?.billNo || this.billId}.pdf`);
  }

  sendMail() {
    this.snackBar.open('Purchase bill mail template configured nahi mila. Mail compose open nahi kiya gaya.', 'Close', {
      duration: 4000
    });
  }

  onSignatureToggle(checked: boolean) {
    if (this.includeSignature === checked) {
      return;
    }

    this.includeSignature = checked;
    this.generatePdf();
  }

  closePreview() {
    this.dialogRef?.close();
  }

  private buildPreviewData(source: any) {
    const company = this.pickEntity(source, ['company', 'companymaster', 'companydetails']);
    const vendor = this.pickEntity(source, ['vendor', 'vendorMaster', 'party', 'partymaster']);
    const warehouse = this.pickEntity(source, ['warehouse', 'whmaster', 'branchwarehouse']);
    const grnCards = this.extractGrnCards(source);
    const rawItems = this.extractItems(source);
    const items = rawItems.map((item: any, index: number) => ({
      sr: index + 1,
      productName: this.safeValue(item.productname || item.description || item.itemname),
      hsn: this.safeValue(item.hsncode || item.hsnsacname || item.hsnsac || item.hsn),
      uom: this.safeValue(item.uomname || item.uom),
      qty: this.formatNumber(item.quantity || 0),
      rate: this.formatCurrency(item.productrate || item.rate || 0, true),
      discount: this.formatNumber(item.disper || 0),
      taxable: this.formatCurrency(item.taxableamount || item.taxableamt || 0, true),
      tax: this.formatCurrency(item.taxamount || item.taxamt || 0, true),
      lineTotal: this.formatCurrency(item.netamount || item.netamt || 0, true),
      remarks: this.safeValue(item.remarks, ''),
      poCode: this.safeValue(item.pocode || item.porefno || '', ''),
      grnCode: this.safeValue(item.grncode || '', '')
    }));
    const taxBreakup = this.buildTaxBreakup(rawItems);

    const netAmount = Number(source.netbillamt || 0);
    const invoiceAmount = Number(source.billamt || 0);
    const taxAmount = Number(source.taxamt || 0);
    const taxableAmount = Number(source.taxableamt || 0);
    const tdsAmount = Number(source.tdsamount || 0);
    const roundOff = Number(source.roundoffamt || 0);

    return {
      logo: this.resolveImage([
        company.logoBase64,
        company.logobase64,
        company.logo,
        source.logoBase64,
        source.logobase64
      ]),
      signatureImage: this.resolveImage([
        company.signature,
        company.signatureBase64,
        company.signaturebase64,
        source.signature,
        source.signatureBase64
      ]),
      stampImage: this.resolveImage([
        company.stamp,
        company.stampBase64,
        company.stampbase64,
        source.stamp,
        source.stampBase64
      ]),
      companyInitials: this.getInitials(company.name),
      companyName: this.safeValue(company.name, 'VCS ERP'),
      companyNameTitle: this.safeValue(company.name, 'VCS ERP'),
      companyAddress: this.joinAddress([company.address1, company.address2, company.city, company.state, company.country, company.pincode]),
      companyMeta: this.buildCompanyMeta(company),
      signatoryName: this.safeValue(
        company.authorized_signatory_name ||
        company.authorizedSignatoryName ||
        source.authorized_signatory_name ||
        source.authorizedSignatoryName,
        ''
      ),
      signatoryTitle: this.safeValue(
        company.authorized_signatory_title ||
        company.authorizedSignatoryTitle ||
        source.authorized_signatory_title ||
        source.authorizedSignatoryTitle,
        'Authorized Signatory'
      ),
      billNo: this.safeValue(source.billno || source.code || source.purchasebillno),
      billDate: this.formatDateValue(source.billdate),
      partyInvNo: this.safeValue(source.partyinvno, ''),
      partyInvDate: this.formatDateValue(source.partyinvdate),
      billType: source.isgrnbill ? 'GRN Bill' : 'Direct Purchase Bill',
      vendorName: this.safeValue(vendor.name || vendor.vendorname || source.vendorname),
      vendorAddress: this.joinAddress([vendor.address1, vendor.address2, vendor.city, vendor.state, vendor.country, vendor.pincode]),
      vendorMeta: [
        this.metaLine('GSTIN', vendor.gstin || vendor.gstnumber),
        this.metaLine('PAN', vendor.pan || vendor.panno),
        this.metaLine('Phone', vendor.mobile || vendor.phone),
        this.metaLine('Email', vendor.email || vendor.emailid)
      ].filter(Boolean),
      warehouseName: this.safeValue(warehouse.name || warehouse.whname || source.warehousename),
      warehouseAddress: this.firstMeaningfulText([
        warehouse.full_address,
        warehouse.fulladdress,
        this.joinAddress([warehouse.address1, warehouse.address2, warehouse.city, warehouse.state, warehouse.country, warehouse.pincode])
      ]),
      overview: [
        { label: 'Vendor', value: this.safeValue(vendor.name || vendor.vendorname || source.vendorname) },
        { label: 'Bill Type', value: source.isgrnbill ? 'GRN Bill' : 'Direct Bill' },
        { label: 'Invoice Ref', value: this.safeValue(source.partyinvno, '-') },
        { label: 'Net Amount', value: this.formatCurrency(netAmount, true) }
      ],
      statusFlags: [
        { label: 'RCM', value: source.isrcm ? 'Yes' : 'No' },
        { label: 'ITC Blocked', value: source.isitcblocked ? 'Yes' : 'No' },
        { label: 'TDS', value: source.istds ? 'Yes' : 'No' },
        { label: 'Cash Bill', value: source.iscashbill ? 'Yes' : 'No' }
      ],
      items,
      taxBreakup,
      grnCards,
      taxableAmount: this.formatCurrency(taxableAmount, true),
      taxAmount: this.formatCurrency(taxAmount, true),
      tdsAmount: this.formatCurrency(tdsAmount, true),
      invoiceAmount: this.formatCurrency(invoiceAmount, true),
      roundOffAmount: this.formatCurrency(roundOff, true),
      netAmount: this.formatCurrency(netAmount, true),
      amountInWords: `${toWords.convert(netAmount, { currency: true })}/-`,
      remarks: this.safeValue(source.remarks, '')
    };
  }

  private generatePdf() {
    if (!this.previewData) {
      return;
    }

    const body = [
      [
        { text: 'SR', style: 'tableHeader' },
        { text: 'DESCRIPTION', style: 'tableHeader' },
        { text: 'HSN / SAC', style: 'tableHeader' },
        { text: 'UOM', style: 'tableHeader' },
        { text: 'QTY', style: 'tableHeader' },
        { text: 'RATE', style: 'tableHeader' },
        { text: 'DIS %', style: 'tableHeader' },
        { text: 'TAXABLE', style: 'tableHeader' },
        { text: 'TAX', style: 'tableHeader' },
        { text: 'LINE TOTAL', style: 'tableHeader' }
      ],
      ...this.previewData.items.map((item: any) => ([
        { text: String(item.sr), alignment: 'center' },
        {
          stack: [
            { text: item.productName },
            ...(item.remarks && item.remarks !== '-' ? [{ text: `[${item.remarks}]`, fontSize: 8, color: '#64748b', italics: true }] : []),
            ...((item.poCode || item.grnCode) ? [{ text: [item.poCode ? `PO: ${item.poCode}` : '', item.grnCode ? `  GRN: ${item.grnCode}` : ''].join('').trim(), fontSize: 8, color: '#475569' }] : [])
          ]
        },
        { text: item.hsn || '-', alignment: 'center' },
        { text: item.uom, alignment: 'center' },
        { text: item.qty, alignment: 'right' },
        { text: item.rate, alignment: 'right' },
        { text: item.discount, alignment: 'right' },
        { text: item.taxable, alignment: 'right' },
        { text: item.tax, alignment: 'right' },
        { text: item.lineTotal, alignment: 'right', bold: true }
      ])),
      [
        { text: 'GRAND TOTAL', colSpan: 9, alignment: 'right', bold: true, fillColor: '#eef2ff', margin: [0, 3, 8, 3] },
        {}, {}, {}, {}, {}, {}, {}, {},
        { text: this.previewData.netAmount, alignment: 'right', bold: true, fillColor: '#eef2ff', margin: [0, 3, 0, 3] }
      ]
    ];

    const summaryBody = [
      [{ text: 'Taxable Amount', style: 'summaryLabel' }, { text: this.previewData.taxableAmount, style: 'summaryValue' }],
      [{ text: 'Tax Amount', style: 'summaryLabel' }, { text: this.previewData.taxAmount, style: 'summaryValue' }],
      [{ text: 'TDS Amount', style: 'summaryLabel' }, { text: this.previewData.tdsAmount, style: 'summaryValue' }],
      [{ text: 'Invoice Amount', style: 'summaryLabel' }, { text: this.previewData.invoiceAmount, style: 'summaryValue' }],
      [{ text: 'Round Off', style: 'summaryLabel' }, { text: this.previewData.roundOffAmount, style: 'summaryValue' }],
      [{ text: 'Net Amount', style: 'summaryTotalLabel' }, { text: this.previewData.netAmount, style: 'summaryTotalValue' }]
    ];
    const taxBreakupBody = [
      [
        { text: 'TAX HEAD', style: 'tableHeader' },
        { text: 'RATE %', style: 'tableHeader' },
        { text: 'TAXABLE', style: 'tableHeader' },
        { text: 'TAX AMOUNT', style: 'tableHeader' }
      ],
      ...this.previewData.taxBreakup.map((tax: any) => ([
        { text: tax.name },
        { text: tax.rate, alignment: 'right' },
        { text: tax.taxableAmount, alignment: 'right' },
        { text: tax.taxAmount, alignment: 'right', bold: true }
      ]))
    ];

    const grnCardColumns = this.previewData.grnCards.length
      ? this.previewData.grnCards.map((card: any) => ({
          width: '*',
          stack: [
            { text: `${card.poCode || '-'} / ${card.grnCode || '-'}`, bold: true, color: '#11284c' },
            { text: `PO Date: ${card.poDate || '-'}`, fontSize: 8.3, color: '#475569' },
            { text: `GRN Date: ${card.grnDate || '-'}`, fontSize: 8.3, color: '#475569' }
          ],
          fillColor: '#f8fafc',
          margin: [8, 8, 8, 8]
        }))
      : [];

    const docDefinition: any = {
      pageSize: 'A4',
      pageMargins: [20, 12, 20, 42],
      footer: (currentPage: number, pageCount: number) => ({
        margin: [20, 0, 20, 6],
        stack: [
          {
            canvas: [
              { type: 'line', x1: 0, y1: 0, x2: 555, y2: 0, lineWidth: 1, color: '#8da0b8' },
              { type: 'rect', x: 0, y: 5, w: 132, h: 2.6, color: '#153a6b' },
              { type: 'rect', x: 441, y: 5, w: 114, h: 2.6, color: '#d97706' }
            ]
          },
          {
            margin: [0, 6, 0, 0],
            columns: [
              { width: '*', text: 'Generated using VCS ERP', style: 'footerText' },
              { width: 90, text: `Page ${currentPage} of ${pageCount}`, style: 'footerPageText', alignment: 'right' }
            ]
          }
        ]
      }),
      background: this.previewData.logo
        ? { image: this.previewData.logo, alignment: 'center', width: 300, height: 300, opacity: 0.05, marginTop: 220 }
        : undefined,
      content: [
        {
          canvas: [
            { type: 'rect', x: 0, y: 0, w: 551, h: 6, color: '#153a6b' },
            { type: 'rect', x: 405, y: 0, w: 146, h: 6, color: '#d97706' },
            { type: 'line', x1: 0, y1: 15, x2: 551, y2: 15, lineWidth: 0.7, color: '#e2e8f0' }
          ],
          margin: [0, -10, 0, 4]
        },
        {
          table: {
            widths: [74, '*', 160],
            body: [[
              {
                stack: this.previewData.logo
                  ? [{ image: this.previewData.logo, width: 66, fit: [66, 66], alignment: 'center' }]
                  : [{ text: this.previewData.companyInitials, style: 'logoFallback' }],
                border: [false, false, false, false],
                margin: [0, 6, 0, 0]
              },
              {
                stack: [
                  { text: this.previewData.companyName.toUpperCase(), style: 'companyHeader' },
                  this.previewData.companyAddress ? { text: this.previewData.companyAddress, style: 'companyMeta' } : '',
                  ...this.previewData.companyMeta.map((text: string) => ({ text, style: 'companyMeta' }))
                ].filter(Boolean),
                border: [false, false, false, false],
                margin: [0, 4, 0, 0]
              },
              {
                stack: [
                  { text: 'PURCHASE BILL', style: 'invoiceHeaderBadge' },
                  { text: `Bill No: ${this.previewData.billNo}`, style: 'invoiceMetaStrong' },
                  { text: `Bill Date: ${this.previewData.billDate}`, style: 'invoiceMeta' },
                  { text: `Party Inv No: ${this.previewData.partyInvNo || '-'}`, style: 'invoiceMeta' },
                  { text: `Party Inv Date: ${this.previewData.partyInvDate || '-'}`, style: 'invoiceMeta' }
                ],
                fillColor: '#f6f8ff',
                margin: [10, 8, 10, 8],
                border: [true, true, true, true]
              }
            ]]
          },
          layout: {
            hLineColor: () => '#d7deea',
            vLineColor: () => '#d7deea'
          }
        },
        {
          columns: this.previewData.overview.map((card: any) => ({
            width: '*',
            stack: [
              { text: card.label, style: 'cardTitle' },
              { text: card.value, style: 'cardHeading' }
            ],
            margin: [8, 8, 8, 8],
            fillColor: '#f8fafc'
          })),
          columnGap: 12,
          margin: [0, 10, 0, 8]
        },
        {
          columns: [
            {
              width: '*',
              stack: [
                { text: 'Vendor Information', style: 'cardTitle' },
                { text: this.previewData.vendorName, style: 'cardHeading' },
                ...(this.previewData.vendorAddress ? [{ text: this.previewData.vendorAddress, style: 'cardText' }] : []),
                ...this.previewData.vendorMeta.map((text: string) => ({ text, style: 'cardText' }))
              ],
              margin: [8, 8, 8, 8],
              fillColor: '#f8fafc'
            },
            {
              width: '*',
              stack: [
                { text: 'Warehouse', style: 'cardTitle' },
                { text: this.previewData.warehouseName, style: 'cardHeading' },
                ...(this.previewData.warehouseAddress ? [{ text: this.previewData.warehouseAddress, style: 'cardText' }] : []),
                ...this.previewData.statusFlags.map((flag: any) => ({ text: `${flag.label}: ${flag.value}`, style: 'cardText' }))
              ],
              margin: [8, 8, 8, 8],
              fillColor: '#f8fafc'
            }
          ],
          columnGap: 14,
          margin: [0, 0, 0, 8]
        },
        ...(grnCardColumns.length
          ? [{
              stack: [
                { text: 'Linked PO / GRN References', style: 'sectionHeader' },
                { columns: grnCardColumns, columnGap: 8 }
              ],
              margin: [0, 0, 0, 10]
            }]
          : []),
        {
          table: {
            headerRows: 1,
            widths: [20, '*', 46, 34, 34, 52, 40, 54, 42, 56],
            body
          },
          layout: {
            fillColor: (rowIndex: number, node: any) => {
              if (rowIndex === 0) {
                return '#0f172a';
              }
              if (rowIndex === node.table.body.length - 1) {
                return '#eef2ff';
              }
              return rowIndex % 2 === 0 ? '#f8fafc' : '#ffffff';
            },
            hLineColor: () => '#93a4bb',
            vLineColor: () => '#a7b5c8',
            hLineWidth: () => 0.8,
            vLineWidth: () => 0.8,
            paddingTop: () => 5,
            paddingBottom: () => 5,
            paddingLeft: () => 5,
            paddingRight: () => 5
          }
        },
        {
          columns: [
            {
              width: '*',
              stack: [
                { text: 'Amount In Words', style: 'sectionHeader' },
                { text: this.previewData.amountInWords, style: 'amountWords' },
                ...(this.previewData.taxBreakup.length
                  ? [{
                      text: 'Tax Breakup',
                      style: 'sectionHeader',
                      margin: [0, 12, 0, 4]
                    },
                    {
                      table: {
                        widths: ['*', 46, 62, 62],
                        body: taxBreakupBody
                      },
                      layout: {
                        fillColor: (rowIndex: number) => rowIndex === 0 ? '#16323f' : rowIndex % 2 === 0 ? '#f8fafc' : '#ffffff',
                        hLineColor: () => '#a7b5c8',
                        vLineColor: () => '#a7b5c8',
                        paddingTop: () => 4,
                        paddingBottom: () => 4,
                        paddingLeft: () => 4,
                        paddingRight: () => 4
                      }
                    }]
                  : []),
                ...(this.previewData.remarks !== '-' ? [{ text: 'Remarks', style: 'sectionHeader', margin: [0, 10, 0, 4] }] : []),
                ...(this.previewData.remarks !== '-' ? [{ text: this.previewData.remarks, style: 'detailText' }] : [])
              ]
            },
            {
              width: 196,
              stack: [
                {
                  table: {
                    widths: ['*', 'auto'],
                    body: summaryBody
                  },
                  layout: {
                    hLineColor: () => '#93a4bb',
                    vLineColor: () => '#a7b5c8',
                    hLineWidth: () => 0.9,
                    vLineWidth: () => 0.9,
                    paddingTop: () => 5,
                    paddingBottom: () => 5,
                    paddingLeft: () => 6,
                    paddingRight: () => 6
                  }
                },
                {
                  stack: [
                    { text: `For ${this.previewData.companyNameTitle}`, style: 'signatoryCompanyInline', alignment: 'right', margin: [0, 0, 0, 4] },
                    this.includeSignature && (this.previewData.stampImage || this.previewData.signatureImage)
                      ? {
                          columns: [
                            {
                              width: 68,
                              stack: [
                                this.previewData.stampImage
                                  ? { image: this.previewData.stampImage, fit: [64, 64], opacity: 0.72, alignment: 'right', margin: [0, 2, -8, 0] }
                                  : { text: '' }
                              ]
                            },
                            {
                              width: 118,
                              stack: [
                                this.previewData.signatureImage
                                  ? { image: this.previewData.signatureImage, fit: [112, 38], alignment: 'right', margin: [0, 8, 0, 0] }
                                  : { text: '' }
                              ]
                            }
                          ],
                          columnGap: -12,
                          margin: [0, 4, 0, 0]
                        }
                      : { text: '', margin: [0, 30, 0, 0] },
                    this.previewData.signatoryName
                      ? { text: this.previewData.signatoryName, style: 'signatoryName', alignment: 'right', margin: [0, 4, 0, 0] }
                      : { text: '', margin: [0, 8, 0, 0] },
                    { text: this.previewData.signatoryTitle || 'Authorized Signatory', style: 'signatoryRole', alignment: 'right' }
                  ],
                  margin: [0, 8, 0, 0]
                }
              ]
            }
          ],
          columnGap: 14,
          margin: [0, 10, 0, 0]
        }
      ],
      styles: {
        logoFallback: { fontSize: 22, bold: true, color: '#0f172a', alignment: 'center', margin: [0, 18, 0, 0] },
        companyHeader: { fontSize: 17, bold: true, color: '#11284c', alignment: 'center' },
        companyMeta: { fontSize: 8.3, color: '#3f526d', margin: [0, 1, 0, 0], alignment: 'center' },
        invoiceHeaderBadge: { fontSize: 14, bold: true, alignment: 'center', color: '#11284c', margin: [0, 0, 0, 8] },
        invoiceMetaStrong: { fontSize: 9.8, bold: true, color: '#11284c', margin: [0, 2, 0, 0] },
        invoiceMeta: { fontSize: 8.8, color: '#43546b', margin: [0, 2, 0, 0] },
        cardTitle: { fontSize: 8.2, bold: true, color: '#1d4ed8', margin: [0, 0, 0, 4] },
        cardHeading: { fontSize: 10.8, bold: true, color: '#11284c', margin: [0, 0, 0, 3] },
        cardText: { fontSize: 8.7, color: '#24364e', margin: [0, 1, 0, 0] },
        sectionHeader: { fontSize: 9, bold: true, color: '#11284c', margin: [0, 0, 0, 4] },
        tableHeader: { bold: true, fontSize: 7.6, color: '#ffffff', alignment: 'center' },
        detailText: { fontSize: 8.6, color: '#24364e', margin: [0, 1, 0, 0] },
        amountWords: { fontSize: 9.1, color: '#11284c', bold: true },
        summaryLabel: { fontSize: 8.5, color: '#43546b', alignment: 'right' },
        summaryValue: { fontSize: 8.8, color: '#11284c', alignment: 'right', bold: true },
        summaryTotalLabel: { fontSize: 9.2, color: '#11284c', alignment: 'right', bold: true },
        summaryTotalValue: { fontSize: 11.6, color: '#c2410c', alignment: 'right', bold: true },
        signatoryCompanyInline: { fontSize: 8.3, bold: true, color: '#334155' },
        signatoryName: { fontSize: 9.2, bold: true, color: '#11284c' },
        signatoryRole: { fontSize: 8.5, color: '#475569' },
        footerText: { fontSize: 6.8, color: '#64748b' },
        footerPageText: { fontSize: 6.8, color: '#64748b' }
      },
      defaultStyle: {
        fontSize: 9,
        color: '#0f172a'
      }
    };

    this.pdfDoc = pdfMake.createPdf(docDefinition);
    this.pdfDoc.getBlob((blob: Blob) => {
      const newBlobUrl = URL.createObjectURL(blob);
      if (this.generatedBlobUrl) {
        URL.revokeObjectURL(this.generatedBlobUrl);
      }
      this.generatedBlobUrl = newBlobUrl;
      this.pdfSrc = `${this.generatedBlobUrl}#toolbar=0&navpanes=0&scrollbar=1&zoom=100`;
    });
  }

  private extractItems(source: any): any[] {
    const candidates = [
      source?.purchasebillitemdetails,
      source?.purchaseBillItemDetails,
      source?.items,
      source?.details
    ];

    for (const candidate of candidates) {
      if (Array.isArray(candidate)) {
        return candidate;
      }
    }

    return [];
  }

  private buildTaxBreakup(items: any[]): Array<{ name: string; rate: string; taxableAmount: string; taxAmount: string }> {
    const taxMap = new Map<string, { name: string; rateValue: number; taxableValue: number; taxValue: number }>();

    items.forEach((item: any) => {
      const taxDetails = Array.isArray(item.purchasebillitemtaxdetails)
        ? item.purchasebillitemtaxdetails
        : Array.isArray(item.purchaseBillItemTaxDetails)
          ? item.purchaseBillItemTaxDetails
          : [];

      taxDetails.forEach((tax: any) => {
        const rateValue = Number(tax.taxrate || 0);
        const taxableValue = Number(tax.taxableamount || 0);
        const taxValue = Number(tax.taxamount || 0);
        const name = this.safeValue(tax.taxname || tax.name || (tax.taxid ? `Tax ${tax.taxid}` : 'Tax'), 'Tax');
        const key = `${name}__${rateValue}`;
        const existing = taxMap.get(key) || {
          name,
          rateValue,
          taxableValue: 0,
          taxValue: 0
        };

        existing.taxableValue += taxableValue;
        existing.taxValue += taxValue;
        taxMap.set(key, existing);
      });
    });

    return Array.from(taxMap.values()).map((row) => ({
      name: row.name,
      rate: this.formatNumber(row.rateValue),
      taxableAmount: this.formatCurrency(row.taxableValue, true),
      taxAmount: this.formatCurrency(row.taxValue, true)
    }));
  }

  private extractGrnCards(source: any): any[] {
    const candidates = [
      source?.purchasebillgrncards,
      source?.purchaseBillGrnCards,
      source?.linkedgrns,
      source?.grncards
    ];

    for (const candidate of candidates) {
      if (Array.isArray(candidate)) {
        return candidate.map((card: any) => ({
          poCode: this.safeValue(card.pocode || card.poCode, ''),
          poDate: this.formatDateValue(card.podate || card.poDate),
          grnCode: this.safeValue(card.grncode || card.grnCode, ''),
          grnDate: this.formatDateValue(card.grndate || card.grnDate)
        }));
      }
    }

    return [];
  }

  private pickEntity(source: any, keys: string[]): any {
    for (const key of keys) {
      const value = source?.[key];
      if (value && typeof value === 'object') {
        return Array.isArray(value) ? value[0] || {} : value;
      }
    }
    return {};
  }

  private buildCompanyMeta(company: any): string[] {
    return [
      this.metaLine('GSTIN', company.gstin),
      this.metaLine('PAN', company.pan),
      this.metaLine('Email', company.email),
      this.metaLine('Website', company.website),
      this.metaLine('Phone', company.mobile || company.phone)
    ].filter(Boolean) as string[];
  }

  private metaLine(label: string, value: any): string {
    const normalized = this.normalizePart(value);
    return normalized ? `${label}: ${normalized}` : '';
  }

  private resolveImage(candidates: any[]): string | null {
    for (const candidate of candidates) {
      const normalized = this.normalizeImageCandidate(candidate);
      if (normalized) {
        return normalized;
      }
    }

    return null;
  }

  private normalizeImageCandidate(value: any): string | null {
    if (typeof value !== 'string') {
      return null;
    }

    const image = value.trim();
    if (!image) {
      return null;
    }

    if (image.startsWith('data:image')) {
      return image;
    }

    if (/^[A-Za-z0-9+/=\r\n]+$/.test(image) && image.length > 120) {
      return `data:image/png;base64,${image.replace(/\s+/g, '')}`;
    }

    return null;
  }

  private firstMeaningfulText(candidates: any[]): string {
    for (const candidate of candidates) {
      const normalized = this.normalizePart(candidate);
      if (normalized) {
        return normalized;
      }
    }

    return '';
  }

  private joinAddress(parts: any[]): string {
    return parts
      .map((part) => this.normalizePart(part))
      .filter(Boolean)
      .join(', ');
  }

  private normalizePart(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }

    const text = String(value).trim();
    return text === '-' ? '' : text;
  }

  private safeValue(value: any, fallback = '-'): string {
    if (value === null || value === undefined || value === '') {
      return fallback;
    }

    return String(value);
  }

  private formatCurrency(value: any, withSymbol = false): string {
    const amount = Number(value || 0);
    if (Number.isNaN(amount)) {
      return this.safeValue(value);
    }

    const formatted = new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);

    return withSymbol ? `${String.fromCharCode(8377)} ${formatted}` : formatted;
  }

  private formatNumber(value: any): string {
    const amount = Number(value || 0);
    if (Number.isNaN(amount)) {
      return this.safeValue(value);
    }

    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount);
  }

  private formatDateValue(value: any): string {
    if (!value) {
      return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return this.safeValue(value);
    }

    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).format(date);
  }

  private getInitials(value: any): string {
    const text = this.safeValue(value, 'PB');
    return text
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0))
      .join('')
      .toUpperCase();
  }
}
